import React, { useState } from "react";
import { useI18n } from "../i18n";
import { api } from "../lib/api";
import { Check, Sparkles, Crown, Zap, ChevronRight } from "lucide-react";
import { getStoredUser } from "../components/RegisterModal";
import TosConsentModal from "../components/TosConsentModal";

const FEATURES = {
  pro: {
    pt: [
      "Mercado global ao vivo (17 moedas, cripto, 12 índices)",
      "100 perguntas/dia à IA com RAG (Claude Sonnet 4.5 + dados reais)",
      "Entrada + saída por voz ilimitada (Whisper + TTS)",
      "Comparador e simulador ilimitados (até 6 ativos)",
      "Watchlist com até 20 ativos",
      "Alertas in-app (cripto, câmbio, índices)",
      "Export de relatórios em PDF",
      "Histórico de chats por 90 dias",
      "Análise de 25 países",
    ],
    en: [
      "Live global market (17 FX, crypto, 12 indices)",
      "100 AI questions/day with RAG (Claude Sonnet 4.5 + real data)",
      "Unlimited voice in/out (Whisper + TTS)",
      "Unlimited comparator and simulator (up to 6 assets)",
      "Watchlist up to 20 assets",
      "In-app alerts (crypto, FX, indices)",
      "PDF report export",
      "Chat history retained 90 days",
      "Analysis of 25 countries",
    ],
  },
  elite: {
    pt: [
      "Tudo do Pro, com tudo ilimitado",
      "IA ilimitada (sem cota diária)",
      "Watchlist ilimitada",
      "Alertas em fila prioritária",
      "Histórico de chats permanente",
      "Comparações salvas e nomeadas",
      "Suporte por email com resposta < 24h",
      "Acesso antecipado a novas features",
      "Privacy+ — todos os direitos LGPD/GDPR",
    ],
    en: [
      "Everything in Pro, all unlimited",
      "Unlimited AI (no daily cap)",
      "Unlimited watchlist",
      "Priority alert queue",
      "Permanent chat history",
      "Named saved comparisons",
      "Email support with < 24h response",
      "Early access to new features",
      "Privacy+ — full LGPD/GDPR rights",
    ],
  },
};

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function Pricing() {
  const { lang } = useI18n();
  const [period, setPeriod] = useState("monthly");
  const [loading, setLoading] = useState(null);
  const [tosOpen, setTosOpen] = useState(false);
  const [pendingPkg, setPendingPkg] = useState(null);
  const user = getStoredUser();

  const copy = {
    kicker: lang === "pt" ? "Planos GOLDORA" : "GOLDORA Plans",
    title: lang === "pt"
      ? "Visão global. Você no controle."
      : "Global vision. You in control.",
    sub: lang === "pt"
      ? "Sem assessor. Sem comissão. Sem conflito de interesses. O cliente decide — a GOLDORA entrega os dados, os gráficos e a IA."
      : "No advisor. No commission. No conflict of interest. You decide — GOLDORA delivers the data, the charts, the AI.",
    monthly: lang === "pt" ? "Mensal" : "Monthly",
    yearly: lang === "pt" ? "Anual" : "Yearly",
    save: lang === "pt" ? "1 mês grátis" : "1 month free",
    forever: lang === "pt" ? "para sempre" : "forever",
    per_month: lang === "pt" ? "/mês" : "/mo",
    per_year: lang === "pt" ? "/ano" : "/yr",
    popular: lang === "pt" ? "Mais escolhido" : "Most chosen",
    wealth: lang === "pt" ? "Wealth tier" : "Wealth tier",
    start_free: lang === "pt" ? "Começar grátis" : "Start free",
    subscribe_pro: lang === "pt" ? "Assinar Pro" : "Go Pro",
    subscribe_elite: lang === "pt" ? "Assinar Elite" : "Go Elite",
    no_card: lang === "pt" ? "Sem cartão para o Explorer" : "No card needed for Explorer",
    trial_badge: lang === "pt" ? "30 dias grátis" : "30 days free",
    trial_caption: lang === "pt"
      ? "Cadastre seu cartão · cancele a qualquer momento durante o teste · zero cobrado se cancelar antes."
      : "Add your card · cancel anytime during trial · zero charged if you cancel before day 30.",
    guarantee: lang === "pt"
      ? "30 dias grátis · Cancele quando quiser · Sem cobrança durante o teste."
      : "30-day free trial · Cancel anytime · No charge during trial.",
    compare_header: lang === "pt" ? "Compare com o mercado" : "Compare to the market",
    tagline_pro: lang === "pt" ? "Para quem decide sozinho" : "For self-directed decision makers",
    tagline_elite: lang === "pt" ? "Para quem tem patrimônio para proteger" : "For those with wealth to protect",
    tagline_free: lang === "pt" ? "Conheça a plataforma" : "Meet the platform",
    billed: lang === "pt" ? "Cobrado anualmente" : "Billed annually",
    vs_bloomberg: lang === "pt"
      ? "Elite custa ~10% do Bloomberg Terminal ($24 k/ano) — com IA conversacional, voz e cobertura global que eles não oferecem."
      : "Elite is ~10% of Bloomberg Terminal's $24 k/yr — with conversational AI, voice and global coverage Bloomberg doesn't offer.",
  };

  const tiers = [
    {
      key: "pro",
      name: "Pro",
      tagline: copy.tagline_pro,
      icon: Zap,
      price: period === "monthly" ? 79 : 869,
      package_id: period === "monthly" ? "pro_monthly" : "pro_yearly",
      cta: copy.subscribe_pro,
      testid: "tier-pro",
      badge: copy.popular,
      accent: "border-gold-500/60 ring-1 ring-gold-500/40 shadow-[0_0_40px_rgba(233,185,52,0.10)]",
    },
    {
      key: "elite",
      name: "Elite",
      tagline: copy.tagline_elite,
      icon: Crown,
      price: period === "monthly" ? 249 : 2739,
      package_id: period === "monthly" ? "elite_monthly" : "elite_yearly",
      cta: copy.subscribe_elite,
      testid: "tier-elite",
      badge: copy.wealth,
      accent: "border-gold-400/40",
    },
  ];

  const startCheckout = async (pkg_id) => {
    if (!pkg_id) return;
    // STEP 1 — show ToS consent modal first. Real checkout only fires after the user agrees.
    setPendingPkg(pkg_id);
    setTosOpen(true);
  };

  const proceedToStripe = async (consent) => {
    const pkg_id = pendingPkg;
    setTosOpen(false);
    if (!pkg_id) return;
    setLoading(pkg_id);
    try {
      // Persist acceptance locally so we don't re-prompt on resume
      try {
        localStorage.setItem("goldora_tos_accepted", JSON.stringify(consent));
      } catch {/* noop */}
      // Best-effort backend record (non-blocking)
      api.post("/tos/accept", {
        email: user?.email || "",
        tos_version: consent.tos_version,
        accepted_at: consent.accepted_at,
        package_id: pkg_id,
      }).catch(() => {/* fire-and-forget */});

      const origin = window.location.origin;
      const res = await api.post("/payments/checkout", {
        package_id: pkg_id,
        origin_url: origin,
        email: user?.email || "",
        lang,
        tos_version: consent.tos_version,
        tos_accepted_at: consent.accepted_at,
      });
      if (res.data?.url) window.location.href = res.data.url;
    } catch (e) {
      console.error(e);
      alert(lang === "pt" ? "Não foi possível iniciar o pagamento." : "Could not start payment.");
    } finally {
      setLoading(null);
      setPendingPkg(null);
    }
  };

  // Compute the plan label for the consent modal
  const pendingTier = pendingPkg ? (pendingPkg.startsWith("pro") ? "Pro" : "Elite") : "";
  const pendingPrice = pendingPkg
    ? (pendingPkg === "pro_monthly" ? "$79/mo" :
       pendingPkg === "pro_yearly" ? "$869/yr" :
       pendingPkg === "elite_monthly" ? "$249/mo" :
       pendingPkg === "elite_yearly" ? "$2,739/yr" : "")
    : "";

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-16" data-testid="page-pricing">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-gold-500/30 bg-gold-500/5 px-3 py-1 text-xs uppercase tracking-[0.2em] text-gold-400 font-mono">
          <Sparkles size={12} />
          {copy.kicker}
        </div>
        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl mx-auto leading-[1.05]">
          {copy.title}
        </h1>
        <p className="mt-5 max-w-2xl mx-auto text-neutral-400 text-lg leading-relaxed">
          {copy.sub}
        </p>

        <div className="mt-10 inline-flex items-center gap-1 bg-white/5 border border-white/10 rounded-full p-1" data-testid="period-toggle">
          <button
            onClick={() => setPeriod("monthly")}
            data-testid="period-monthly"
            className={`px-5 py-2 rounded-full text-sm transition-colors ${
              period === "monthly" ? "bg-gold-500 text-black font-medium" : "text-neutral-400 hover:text-white"
            }`}
          >
            {copy.monthly}
          </button>
          <button
            onClick={() => setPeriod("yearly")}
            data-testid="period-yearly"
            className={`px-5 py-2 rounded-full text-sm transition-colors flex items-center gap-2 ${
              period === "yearly" ? "bg-gold-500 text-black font-medium" : "text-neutral-400 hover:text-white"
            }`}
          >
            {copy.yearly}
            <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${
              period === "yearly" ? "bg-black/20 text-black" : "bg-gold-500/15 text-gold-400"
            }`}>
              {copy.save}
            </span>
          </button>
        </div>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-testid="pricing-grid">
        {tiers.map((tier) => {
          const Icon = tier.icon;
          return (
            <div
              key={tier.key}
              data-testid={tier.testid}
              className={`relative surface p-7 md:p-8 flex flex-col ${tier.accent} transition-transform duration-300 hover:-translate-y-0.5`}
            >
              {tier.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-mono uppercase tracking-[0.2em] px-2.5 py-1 rounded-full whitespace-nowrap ${
                  tier.key === "pro" ? "bg-gold-500 text-black" : "bg-black border border-gold-400/60 text-gold-300"
                }`}>
                  {tier.badge}
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-md bg-white/5 border border-white/10 grid place-items-center text-gold-400">
                  <Icon size={16} />
                </div>
                <div>
                  <div className="font-brand text-xl brand-metal tracking-[0.14em]">{tier.name}</div>
                  <div className="text-xs text-neutral-500">{tier.tagline}</div>
                </div>
              </div>

              <div className="mt-7">
                <div className="flex items-baseline gap-1">
                  <span className="font-mono text-5xl tracking-tight text-white">
                    {USD.format(tier.price)}
                  </span>
                  <span className="text-neutral-500 text-sm font-mono">
                    {period === "monthly" ? copy.per_month : copy.per_year}
                  </span>
                </div>
                {period === "yearly" && (
                  <div className="text-xs text-gold-400 mt-1 font-mono uppercase tracking-widest">
                    {USD.format(Math.round(tier.price / 12))}{copy.per_month} · {copy.billed}
                  </div>
                )}
              </div>

              <button
                onClick={() => startCheckout(tier.package_id)}
                disabled={loading === tier.package_id}
                data-testid={`cta-${tier.key}`}
                className={`mt-6 w-full font-medium py-3 rounded-md transition-colors text-sm flex items-center justify-center gap-1.5 ${
                  tier.key === "pro"
                    ? "bg-gold-500 hover:bg-gold-400 text-black"
                    : "bg-black border border-gold-400/50 hover:border-gold-400 hover:bg-gold-500/10 text-gold-200"
                } disabled:opacity-40`}
              >
                {loading === tier.package_id ? "…" : tier.cta}
                {loading !== tier.package_id && <ChevronRight size={14} />}
              </button>

              <p className="mt-3 text-center text-[11px] text-gold-400 font-mono uppercase tracking-[0.18em]" data-testid={`trial-${tier.key}`}>
                ✦ {copy.trial_badge}
              </p>

              <ul className="mt-7 space-y-2.5 text-sm text-neutral-300" data-testid={`features-${tier.key}`}>
                {(FEATURES[tier.key][lang] || FEATURES[tier.key].en).map((f, i) => (
                  <li key={i} className="flex gap-2.5">
                    <Check size={15} className="text-gold-400 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {tier.key === "elite" && (
                <p className="mt-5 text-[11px] text-gold-500/80 leading-relaxed">
                  ◆ {copy.vs_bloomberg}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-10 text-center text-sm text-neutral-400 font-mono">
        ◆ {copy.guarantee}
      </div>

      <div className="mt-16 surface p-6 md:p-10" data-testid="competitor-compare">
        <h3 className="font-display text-2xl sm:text-3xl tracking-tight">{copy.compare_header}</h3>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-xs uppercase tracking-[0.18em] text-neutral-500 border-b border-white/10">
                <th className="text-left py-3 px-2 font-mono">Feature</th>
                <th className="text-center py-3 px-2 font-mono text-gold-400">GOLDORA Elite</th>
                <th className="text-center py-3 px-2 font-mono">Bloomberg</th>
                <th className="text-center py-3 px-2 font-mono">{lang === "pt" ? "Bancos privados" : "Private Banks"}</th>
                <th className="text-center py-3 px-2 font-mono">Yahoo Finance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {[
                { f: lang === "pt" ? "Preço anual" : "Yearly price", g: "$2,739", b: "$24,000+", bk: lang === "pt" ? "% dos ativos" : "% of AUM", y: lang === "pt" ? "Grátis" : "Free" },
                { f: lang === "pt" ? "IA conversacional" : "Conversational AI", g: "✓ Claude 4.5", b: lang === "pt" ? "Limitada" : "Limited", bk: "✗", y: "✗" },
                { f: lang === "pt" ? "Voz (in + out)" : "Voice (in + out)", g: "✓ 8 idiomas", b: "✗", bk: "✗", y: "✗" },
                { f: lang === "pt" ? "Comparador cruzado" : "Cross-asset compare", g: "✓ 6 ativos", b: lang === "pt" ? "Parcial" : "Partial", bk: "✗", y: "✗" },
                { f: lang === "pt" ? "Imóveis + carros/país" : "Real estate + cars/country", g: "✓ 25 países", b: "✗", bk: "✗", y: "✗" },
                { f: lang === "pt" ? "Conflito de interesse" : "Conflict of interest", g: lang === "pt" ? "Nenhum" : "None", b: lang === "pt" ? "Nenhum" : "None", bk: lang === "pt" ? "Comissões" : "Commissions", y: lang === "pt" ? "Anúncios" : "Ads" },
                { f: lang === "pt" ? "Exige patrimônio?" : "Net-worth required?", g: "✗", b: "✗", bk: "✓", y: "✗" },
              ].map((r, i) => (
                <tr key={i} className="text-neutral-300">
                  <td className="py-3 px-2 font-medium">{r.f}</td>
                  <td className="py-3 px-2 text-center text-gold-300">{r.g}</td>
                  <td className="py-3 px-2 text-center text-neutral-400">{r.b}</td>
                  <td className="py-3 px-2 text-center text-neutral-400">{r.bk}</td>
                  <td className="py-3 px-2 text-center text-neutral-400">{r.y}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-neutral-500 leading-relaxed">
          {lang === "pt"
            ? "* Bancos privados cobram 1 – 2% ao ano sobre o patrimônio (AUM). Num portfólio de $2 M, isso é $20 k – $40 k/ano — e ainda cobram comissão sobre o que vendem. GOLDORA é SaaS puro: preço fixo, zero conflito."
            : "* Private banks charge 1 – 2% yearly on AUM. On a $2 M portfolio, that's $20 k – $40 k/yr — plus commissions on what they sell you. GOLDORA is pure SaaS: flat price, zero conflict."}
        </p>
      </div>

      {/* Wealth tier closing CTA */}
      <div className="mt-14 relative overflow-hidden surface p-8 md:p-12 text-center">
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{background:"radial-gradient(circle at 50% 0%, rgba(233,185,52,0.18) 0%, transparent 60%)"}} />
        <div className="relative">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-gold-500">◆ {copy.wealth} ◆</div>
          <h3 className="mt-4 font-brand text-3xl sm:text-4xl brand-metal tracking-[0.12em]">ELITE</h3>
          <p className="mt-4 max-w-xl mx-auto text-neutral-300">
            {lang === "pt"
              ? "Quem tem patrimônio não escolhe pelo preço — escolhe pelo alinhamento. Elite é para quem quer inteligência premium sem passar pelo filtro de um banco privado que vive de comissão."
              : "People with wealth don't pick by price — they pick by alignment. Elite is for those who want premium intelligence without going through a private bank that lives on commissions."}
          </p>
          <button
            onClick={() => startCheckout(period === "monthly" ? "elite_monthly" : "elite_yearly")}
            data-testid="cta-elite-bottom"
            className="mt-7 inline-flex items-center gap-2 bg-black border border-gold-400/60 hover:border-gold-300 hover:bg-gold-500/10 text-gold-200 font-medium px-7 py-3 rounded-md transition-colors"
          >
            {copy.subscribe_elite}
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <TosConsentModal
        open={tosOpen}
        planLabel={pendingTier}
        priceLabel={pendingPrice}
        trialDays={30}
        email={user?.email || ""}
        onCancel={() => { setTosOpen(false); setPendingPkg(null); }}
        onAgree={proceedToStripe}
      />
    </div>
  );
}
